import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    CalendarTodayOutlined,
    KeyboardArrowDownOutlined,
    Restore,
    AccessTime,
    Check,
    Clear,
} from '@mui/icons-material';
import { getDateTextPicker, getStatus, useJunoCommonFunctions } from '../../client/Juno/helpers';
import * as actions from '../../client/Redux/actions';
import { TrandDown, TrandUp } from '../utils/Images';
import { useNavigate } from 'react-router-dom';
import TransactionsHistoryTable from './transactions/TransactionsHistoryTable';
import JunoButton from './common/JunoButton';
import MobileFilterModal from './transactions/MobileFilterModal';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterWrapper from './transactions/FilterWrapper';
import ActiveFilters from './transactions/ActiveFilters';

const DATE_OPTIONS = [
    { key: 'today', label: 'Today', treadText: 'Today' },
    { key: 'lastWeek', label: 'Last Week', treadText: 'last week' },
    { key: 'lastMonth', label: 'Last Month', treadText: 'last month' },
    { key: 'lastQuarter', label: 'Last Quarter', treadText: 'last quarter' },
    { key: 'lastYear', label: 'Last Year', treadText: 'last year' },
];

const ICONS = {
    Restore: <Restore className="dark:text-white text-lg !w-[18px] !h-[18px]" />,
    AccessTime: <AccessTime className="dark:text-white text-lg !w-[18px] !h-[18px]" />,
    Check: <Check className="dark:text-white text-lg !w-[18px] !h-[18px]" />,
    Clear: <Clear className="dark:text-white text-lg !w-[18px] !h-[18px]" />,
};

const SkeletonCard = React.memo(({ isLast }) => (
    <div className="min-w-[280px] sm:min-w-0 sm:w-1/2 lg:w-1/4">
        <div className="flex p-3 bg-[#FFFFFF] dark:bg-[#26272B]  h-full">
            <div className="flex items-start w-full gap-4">
                {/* Circle Skeleton */}
                <div className="w-10 h-10 rounded-full bg-neutral-200 dark:bg-neutral-700 animate-pulse"></div>

                {/* Text Skeletons */}
                <div className="flex flex-col gap-2 mt-1">
                    <div className="h-4 w-32 rounded bg-neutral-200 dark:bg-neutral-700 animate-pulse"></div>
                    <div className="h-4 w-24 rounded bg-neutral-200 dark:bg-neutral-700 animate-pulse"></div>
                </div>
            </div>
        </div>
    </div>
));

export const DatePicker = React.memo(({ isOpen, toggle, selectedKey, onChange }) => {
    const handleOptionClick = option => {
        onChange(option);
        toggle(false);
    };

    return (
        <div
            className="relative flex items-center border-2 h-7 border-input-border rounded-lg cursor-pointer w-full md:w-auto"
            onClick={() => toggle(!isOpen)}>
            <div className="flex p-1 justify-between items-center w-full min-w-[180px]">
                <CalendarTodayOutlined style={{ fontSize: 16 }} className="!text-text" />
                <button
                    data-e2e={DATE_OPTIONS.find(opt => opt.key === selectedKey)?.label}
                    className="text-sm font-semibold bg-transparent text-gray-900 !text-text">
                    {DATE_OPTIONS.find(opt => opt.key === selectedKey)?.label}
                </button>
                <KeyboardArrowDownOutlined className="!text-text" />
            </div>
            {isOpen && (
                <div className="absolute top-full mt-2 bg-white borde rounded-lg shadow-lg z-10 w-full">
                    <ul className="text-sm !text-text bg-modal-bg">
                        {DATE_OPTIONS.map(({ key, label, treadText }) => (
                            <li
                                data-e2e={label}
                                key={key}
                                className="py-2 px-4 hover:bg-card-bg cursor-pointer"
                                onClick={e => {
                                    e.stopPropagation();
                                    handleOptionClick({ key, label, treadText });
                                }}>
                                {label}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
});

const OverviewCard = React.memo(({ data, imageBaseUrl, treadText }) => {
    const { amount, label, bgIcon, iconType, trand = 'down', tradingInfo } = data;

    return (
        <div className="min-w-[280px] sm:min-w-0 sm:w-1/2 lg:w-1/4">
            <div className="flex p-[12px] bg-[#F4F4F5] dark:bg-[#3F3F46] h-full rounded-[6px]">
                <div className="w-full flex flex-col gap-4">
                    <div className="flex justify-between w-full">
                        <div className="flex flex-col gap-1">
                            <h2
                                className="text-[28px] font-semibold text-text"
                                data-e2e={amount}
                                data-e2e-label={label}>
                                {amount}
                            </h2>
                            <p className="text-xs font-medium text-text uppercase">{label}</p>
                        </div>
                        <div
                            className={`sm:w-10 sm:h-10 !w-8 !h-8 flex items-center justify-center bg-[#E4E4E7] dark:bg-[#26272B] font-bold rounded-full`}>
                            {ICONS[iconType]}
                        </div>
                    </div>

                    <div className="flex items-center gap-[6px]">
                        <img
                            src={`${imageBaseUrl}${trand === 'up' ? TrandUp : TrandDown}`}
                            alt="trend-icon"
                            className="h-5 w-5"
                        />
                        <p className="text-sm text-balanceCard-manage font-medium uppercase">
                            {tradingInfo || `-0.00% this ${treadText}`}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
});

function TransactionOverview({ isDashboard }) {
    const dispatch = useDispatch();
    const imageBaseUrl = useSelector(state => state.config.image_base_url);
    const clientData = useSelector(state => state.juno.clientData);
    const selectedDateOption = useSelector(state => state.juno.selectedTranasctionOverview);
    const overviewData = useSelector(state => state.juno.overviewData);
    const totalTransactionCount = useSelector(state => state.juno.totalTransactionCount);

    const { fetchTransactionOverview, useWindowWidth } = useJunoCommonFunctions();

    const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const defaultFilter = {
        currency: [],
        type: [],
        currencyType: [],
        from: '',
        to: '',
        status: [],
    };
    const [currentFilters, setCurrentFilters] = useState(defaultFilter);
    const [open, setOpen] = useState(false);
    const windowWidth = useWindowWidth();

    const handleDateChange = React.useCallback(
        selectedOption => {
            dispatch(actions.setSelectedTranasctionOverview(selectedOption));
        },
        [dispatch],
    );

    const toggleDatepicker = React.useCallback(value => {
        setIsDatepickerOpen(value !== undefined ? value : prev => !prev);
    }, []);

    useEffect(() => {
        (async () => {
            if (clientData?.clientId) {
                setIsLoading(true);
                try {
                    await fetchTransactionOverview();
                } finally {
                    setIsLoading(false);
                }
            }
        })();
    }, [clientData, selectedDateOption]);

    const navigate = useNavigate();

    function handleSeeAll() {
        navigate('/client/transactions');
        overviewData;
    }

    const handleFiltersChange = filters => {
        setCurrentFilters?.(filters);
    };

    const handleClearFilters = () => {
        setCurrentFilters(defaultFilter);
    };

    // Remove specific filter function for mobile
    const handleRemoveFilter = key => {
        const newFilters = { ...currentFilters };

        if (key === 'from' || key === 'to') {
            newFilters.from = '';
            newFilters.to = '';
        } else if (key === 'status') {
            newFilters.status = [];
            newFilters.statusDisplay = []; 
        } else if (Array.isArray(newFilters[key])) {
            newFilters[key] = [];
        } else {
            newFilters[key] = '';
        }

        setCurrentFilters(newFilters);
    };

    // Get display text for date filters
    const displayText = getDateTextPicker({
        from: currentFilters.from,
        to: currentFilters.to,
    });

    if (isDashboard && totalTransactionCount === 0) {
        return null;
    }

    return (
        <>
            {!isDashboard && windowWidth <= 1100 && (
                <div className=" !mb-1 w-full !text-text inline-flex h-[52px] px-4 py-[14px] justify-between items-start shrink-0 rounded-b-lg border-b border-white/10 bg-filterPannel-bg ">
                    <p className="!text-text text-center p-2 text-sm font-semibold leading-none">
                        TRANSACTION HISTORY
                    </p>
                    <div
                        className="rounded-full cursor-pointer flex items-center justify-center gap-2"
                        onClick={setOpen}>
                        <FilterListIcon className="!text-primary" />
                    </div>
                </div>
            )}
            <div className="mb-0 px-0 bg-[#FFFFFF] dark:bg-[#26272B] border !border-section-br rounded-md">
                {(isDashboard || windowWidth < 1100) && (
                    <div className="flex w-full flex-col  !rounded-lg rounded-b-none lg:flex-row lg:items-center justify-between space-y-3 px-6 !py-4 md:space-y-0 ">
                        {isDashboard && (
                            <h2 className="font-semibold  text-lg sm:text-sm leading-7 text-left w-full md:w-auto text-uppercase !text-section-title ">
                                Transactions
                            </h2>
                        )}
                        <div className="flex justify-start lg:justify-end gap-2 ">
                            {(isDashboard || windowWidth < 1100) && (
                                <DatePicker
                                    isOpen={isDatepickerOpen}
                                    toggle={toggleDatepicker}
                                    selectedKey={selectedDateOption.key}
                                    onChange={handleDateChange}
                                />
                            )}

                            {isDashboard && (
                                <JunoButton
                                    className="!w-[66px] text-xs !p-0 !h-7 font-semibold hidden lg:block"
                                    type="secondary"
                                    onClick={handleSeeAll}>
                                    See all
                                </JunoButton>
                            )}
                        </div>
                    </div>
                )}

                {/* Selected filter render for Small device */}
                 {!isDashboard && windowWidth <= 1100 && (
                    <div className="px-4 pb-2">
                        <ActiveFilters
                            filters={currentFilters}
                            onClear={handleClearFilters}
                            onRemoveFilter={handleRemoveFilter}
                            getStatus={getStatus}
                            displayText={displayText}
                        />
                    </div>
                )}

                {!isDashboard && windowWidth > 1100 && (
                    <FilterWrapper
                        title="TRANSACTION HISTORY"
                        onFiltersChange={handleFiltersChange}
                        initialFilters={currentFilters}
                        onClose={() => setOpen(false)}
                        isDatepickerOpen={isDatepickerOpen}
                        toggleDatepicker={toggleDatepicker}
                        selectedDateOption={selectedDateOption}
                        handleDateChange={handleDateChange}
                    />
                )}
                {isLoading ? (
                    <div className="flex overflow-x-auto sm:overflow-x-hidden sm:flex-wrap -mx-2">
                        {[...Array(4)].map((_, index) => (
                            <SkeletonCard key={index} isLast={index === 3} />
                        ))}
                    </div>
                ) : overviewData.length > 0 ? (
                    <div
                        className={`flex overflow-x-auto sm:overflow-x-hidden sm:flex-wrap-mx-2 !px-6 !pb-4 !gap-1`}>
                        {overviewData.map(data => (
                            <OverviewCard
                                key={data.key}
                                data={data}
                                imageBaseUrl={imageBaseUrl}
                                treadText={selectedDateOption.treadText}
                            />
                        ))}
                    </div>
                ) : (
                    <p className="text-center text-gray-500 mt-4">No data available</p>
                )}
                <TransactionsHistoryTable
                    isDashboard={isDashboard}
                    currentFilters={currentFilters}
                    windowWidth={windowWidth}
                />
                {windowWidth < 1100 && !isDashboard && open && (
                    <div className="p-4">
                        <MobileFilterModal
                            open={open}
                            onClose={() => setOpen(false)}
                            filter={currentFilters}
                            setFilter={handleFiltersChange}
                        />
                    </div>
                )}
            </div>
        </>
    );
}

export default TransactionOverview;
